import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { decoderPayload, payloadDepuisRoute } from '../../analysis/sync'
import { ingererEvenements, type ResultatIngestion } from '../../db/repo'
import { Carte, Encart, Entete } from '../composants'

/**
 * Point d'arrivee du raccourci du matin.
 *
 * L'URL ouverte par l'automatisation est de la forme :
 *
 *     https://perso2.gesco-app.fr/#/sync/P202608102237-V202608102355-A202608110700
 *
 * La charge est dans le **fragment** : elle n'est pas transmise au serveur, ne
 * figure dans aucun log Apache, et n'existe que dans le navigateur. C'est ce
 * qui permet a l'app de recevoir des donnees sans backend et sans qu'elles
 * transitent nulle part.
 *
 * L'ecran se veut furtif : on ouvre l'app au reveil pour voir sa nuit, pas
 * pour lire un rapport d'import. Il ne s'attarde que si quelque chose n'a pas
 * ete compris.
 */
export function PageSync() {
  const { payload } = useParams()
  const navigate = useNavigate()
  const [resultat, setResultat] = useState<ResultatIngestion | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  // React 18+ en mode strict monte deux fois : sans ce garde, l'ingestion
  // serait lancee en double. Elle est idempotente, mais autant ne pas la
  // declencher pour rien.
  const lance = useRef(false)

  useEffect(() => {
    if (lance.current) return
    lance.current = true

    const brut = payloadDepuisRoute(payload)
    const { evenements, ignores } = decoderPayload(brut)

    ingererEvenements(evenements, ignores)
      .then((r) => {
        setResultat(r)
        // Rien d'anormal : on efface l'etape et on montre la nuit.
        if (r.ignores.length === 0) {
          setTimeout(() => navigate('/', { replace: true }), 900)
        }
      })
      .catch((e: unknown) => {
        console.error(e)
        setErreur(e instanceof Error ? e.message : 'Échec de l’enregistrement.')
      })
  }, [payload, navigate])

  return (
    <>
      <Entete titre="Synchronisation" />
      <main className="contenu">
        {erreur && <Encart ton="danger">{erreur}</Encart>}

        {!resultat && !erreur && <p className="doux">Lecture des relevés…</p>}

        {resultat && (
          <Carte>
            <div className="pile">
              <p>
                <strong>{resultat.recus}</strong>{' '}
                {resultat.recus > 1 ? 'relevés reçus' : 'relevé reçu'}
                {resultat.nouveaux !== resultat.recus && (
                  <span className="doux">
                    {' '}
                    · {resultat.nouveaux} nouveau{resultat.nouveaux > 1 ? 'x' : ''}, le reste
                    était déjà enregistré
                  </span>
                )}
              </p>

              {resultat.recus === 0 && (
                <Encart ton="alerte">
                  Aucun relevé lisible dans cette adresse. Vérifiez que l’automatisation
                  ajoute bien le contenu du fichier à la fin de l’URL.
                </Encart>
              )}

              {resultat.ignores.length > 0 && (
                <Encart ton="alerte">
                  <strong>
                    {resultat.ignores.length} segment
                    {resultat.ignores.length > 1 ? 's' : ''} incompréhensible
                    {resultat.ignores.length > 1 ? 's' : ''}
                  </strong>
                  <p style={{ marginTop: 4 }}>
                    <code>{resultat.ignores.slice(0, 6).join(' · ')}</code>
                  </p>
                  <p style={{ marginTop: 6 }}>
                    Le reste a bien été enregistré. Un raccourci mal configuré se voit ici
                    dès le premier matin, plutôt que par une nuit manquante des semaines
                    plus tard.
                  </p>
                </Encart>
              )}

              <div className="boutons">
                <Link className="btn btn--principal grandir" to="/">
                  Voir la nuit
                </Link>
                {resultat.ignores.length > 0 && (
                  <Link className="btn" to="/installation">
                    Revoir le guide
                  </Link>
                )}
              </div>
            </div>
          </Carte>
        )}
      </main>
    </>
  )
}
